/** Card layouts a person can pick. Order is the order shown in the editor. */
export const LAYOUTS = [
  { id: 'classic', name: 'Classic', description: 'Brand color header with your photo' },
  { id: 'banner', name: 'Banner', description: 'Large cover with a centered photo' },
  { id: 'split', name: 'Split', description: 'Photo beside your name, contact tiles' },
  { id: 'minimal', name: 'Minimal', description: 'Big name, clean lines' },
  { id: 'bold', name: 'Bold', description: 'Full brand color, high impact' },
];

const LAYOUT_IDS = LAYOUTS.map((l) => l.id);
const LEGACY = { default: 'classic', modern: 'split', elegant: 'banner' };

export const DEFAULT_THEME = {
  logo_url: null,
  primary_color: '#0B1530',
  secondary_color: '#FFFFFF',
  accent_color: '#265EE6',
  font_family: 'Instrument Sans',
  layout: 'classic',
  locked_fields: [],
};

export const normalizeLayout = (id) => {
  const v = LEGACY[id] || id;
  return LAYOUT_IDS.includes(v) ? v : 'classic';
};

/**
 * The layout a card shows: the person's own choice, else the company default.
 * When the company locks the layout, its default always wins.
 */
export const cardLayout = (card, theme) =>
  normalizeLayout((theme?.locked_fields || []).includes('layout') ? theme?.layout : card?.layout || theme?.layout);

const rgb = (hex) => {
  const n = parseInt(((hex || '#000000').replace('#', '') + '000000').slice(0, 6), 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
};

/** Relative luminance (WCAG). */
export function luminance(hex) {
  const [r, g, b] = rgb(hex).map((c) => {
    const s = c / 255;
    return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
  });
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

/** Ink or white, whichever reads better on the given background. */
export const onColor = (hex) => (luminance(hex) > 0.4 ? '#0B1530' : '#FFFFFF');

/** Mix a color toward white (amount 0-1), for soft tints. */
export function tint(hex, amount = 0.88) {
  const [r, g, b] = rgb(hex).map((c) => Math.round(c + (255 - c) * amount));
  return `#${((r << 16) | (g << 8) | b).toString(16).padStart(6, '0')}`;
}

/** Darken a color (amount 0-1). */
export function shade(hex, amount = 0.25) {
  const [r, g, b] = rgb(hex).map((c) => Math.max(0, Math.round(c * (1 - amount))));
  return `#${((r << 16) | (g << 8) | b).toString(16).padStart(6, '0')}`;
}

/** Brand theme with defaults filled in and derived colors. */
export function resolveTheme(theme) {
  const t = { ...DEFAULT_THEME, ...(theme || {}) };
  for (const k of ['primary_color', 'accent_color']) {
    if (!/^#[0-9A-Fa-f]{6}$/.test(t[k] || '')) t[k] = DEFAULT_THEME[k];
  }
  return {
    ...t,
    layout: normalizeLayout(t.layout),
    onPrimary: onColor(t.primary_color),
    onAccent: onColor(t.accent_color),
    accentSoft: tint(t.accent_color),
  };
}

export const initials = (name) =>
  (name || '')
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0].toUpperCase())
    .join('') || '?';
